import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Check, ChevronRight, ChevronLeft, Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  profileStepOneSchema,
  profileStepTwoSchema,
  profileStepThreeSchema,
} from "@/lib/validations/profile";

export default function Onboarding() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [stepOneData, setStepOneData] = useState({
    full_name: "",
    phone: "",
    location_city: "",
    date_of_birth: undefined as Date | undefined,
  });

  const [stepTwoData, setStepTwoData] = useState({
    bio: "",
    experience_years: 0,
    skills: [] as string[],
    skillInput: "",
  });

  const [stepThreeData, setStepThreeData] = useState({
    linkedin_url: "",
    github_url: "",
    website_url: "",
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      navigate("/auth");
      return;
    }

    setUser(session.user);

    // Get user role
    const { data: roleData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", session.user.id)
      .single();

    setUserRole(roleData?.role || null);

    // Load existing profile data
    const { data: profileData } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", session.user.id)
      .single();

    if (profileData) {
      setStepOneData({
        full_name: profileData.full_name || "",
        phone: profileData.phone || "",
        location_city: profileData.location_city || "",
        date_of_birth: profileData.date_of_birth ? new Date(profileData.date_of_birth) : undefined,
      });
      setStepTwoData({
        bio: profileData.bio || "",
        experience_years: profileData.experience_years || 0,
        skills: profileData.skills || [],
        skillInput: "",
      });
      setStepThreeData({
        linkedin_url: profileData.linkedin_url || "",
        github_url: profileData.github_url || "",
        website_url: profileData.website_url || "",
      });

      // If onboarding already completed, redirect to dashboard
      if (profileData.onboarding_completed) {
        redirectToDashboard(roleData?.role);
      }
    }
  };

  const redirectToDashboard = (role: string | null) => {
    if (role === "candidate") {
      navigate("/candidate/dashboard");
    } else if (role === "employer_service_provider") {
      navigate("/employer/dashboard");
    } else {
      navigate("/");
    }
  };

  const handleStepOne = async () => {
    setValidationErrors({});

    const validationResult = profileStepOneSchema.safeParse(stepOneData);
    
    if (!validationResult.success) {
      const errors: Record<string, string> = {};
      validationResult.error.issues.forEach((err) => {
        if (err.path[0]) {
          errors[err.path[0].toString()] = err.message;
        }
      });
      setValidationErrors(errors);
      return;
    }

    setCurrentStep(2);
  };

  const handleStepTwo = async () => {
    setValidationErrors({});

    const validationResult = profileStepTwoSchema.safeParse({
      bio: stepTwoData.bio,
      experience_years: stepTwoData.experience_years,
      skills: stepTwoData.skills,
    });
    
    if (!validationResult.success) {
      const errors: Record<string, string> = {};
      validationResult.error.issues.forEach((err) => {
        if (err.path[0]) {
          errors[err.path[0].toString()] = err.message;
        }
      });
      setValidationErrors(errors);
      return;
    }

    setCurrentStep(3);
  };

  const handleFinalSubmit = async () => {
    setIsLoading(true);
    setValidationErrors({});

    const validationResult = profileStepThreeSchema.safeParse(stepThreeData);
    
    if (!validationResult.success) {
      const errors: Record<string, string> = {};
      validationResult.error.issues.forEach((err) => {
        if (err.path[0]) {
          errors[err.path[0].toString()] = err.message;
        }
      });
      setValidationErrors(errors);
      setIsLoading(false);
      return;
    }

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: stepOneData.full_name,
          phone: stepOneData.phone || null,
          location_city: stepOneData.location_city || null,
          date_of_birth: stepOneData.date_of_birth?.toISOString().split('T')[0] || null,
          bio: stepTwoData.bio || null,
          experience_years: stepTwoData.experience_years || null,
          skills: stepTwoData.skills,
          linkedin_url: stepThreeData.linkedin_url || null,
          github_url: stepThreeData.github_url || null,
          website_url: stepThreeData.website_url || null,
          onboarding_completed: true,
        })
        .eq("id", user.id);

      if (error) throw error;

      toast({
        title: "Profile Complete!",
        description: "Welcome to Second Chance Connect. Your profile has been created.",
      });

      redirectToDashboard(userRole);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addSkill = () => {
    if (stepTwoData.skillInput.trim() && stepTwoData.skills.length < 20) {
      setStepTwoData({
        ...stepTwoData,
        skills: [...stepTwoData.skills, stepTwoData.skillInput.trim()],
        skillInput: "",
      });
    }
  };

  const removeSkill = (index: number) => {
    setStepTwoData({
      ...stepTwoData,
      skills: stepTwoData.skills.filter((_, i) => i !== index),
    });
  };

  const totalSteps = 3;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4">
      <div className="max-w-2xl w-full bg-card rounded-2xl shadow-2xl p-8 md:p-12">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-2xl font-bold">Complete Your Profile</h2>
            <span className="text-sm text-muted-foreground">
              Step {currentStep} of {totalSteps}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-4">Basic Information</h3>
              <p className="text-muted-foreground mb-6">
                Let's start with some basic information about you.
              </p>
            </div>

            <div>
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                value={stepOneData.full_name}
                onChange={(e) => setStepOneData({ ...stepOneData, full_name: e.target.value })}
                placeholder="John Doe"
                className="mt-2"
              />
              {validationErrors.full_name && (
                <p className="text-sm text-destructive mt-1">{validationErrors.full_name}</p>
              )}
            </div>

            <div>
              <Label htmlFor="date_of_birth">Date of Birth *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full mt-2 justify-start text-left font-normal",
                      !stepOneData.date_of_birth && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {stepOneData.date_of_birth ? (
                      format(stepOneData.date_of_birth, "PPP")
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={stepOneData.date_of_birth}
                    onSelect={(date) => setStepOneData({ ...stepOneData, date_of_birth: date })}
                    disabled={(date) =>
                      date > new Date() || date < new Date("1900-01-01")
                    }
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              {validationErrors.date_of_birth && (
                <p className="text-sm text-destructive mt-1">{validationErrors.date_of_birth}</p>
              )}
            </div>

            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                value={stepOneData.phone}
                onChange={(e) => setStepOneData({ ...stepOneData, phone: e.target.value })}
                placeholder="+44 7XXX XXXXXX"
                className="mt-2"
              />
              {validationErrors.phone && (
                <p className="text-sm text-destructive mt-1">{validationErrors.phone}</p>
              )}
            </div>

            <div>
              <Label htmlFor="location_city">Location (City)</Label>
              <Input
                id="location_city"
                value={stepOneData.location_city}
                onChange={(e) => setStepOneData({ ...stepOneData, location_city: e.target.value })}
                placeholder="London"
                className="mt-2"
              />
              {validationErrors.location_city && (
                <p className="text-sm text-destructive mt-1">{validationErrors.location_city}</p>
              )}
            </div>

            <Button onClick={handleStepOne} className="w-full" size="lg">
              Continue
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Step 2: Professional Information */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-4">Professional Background</h3>
              <p className="text-muted-foreground mb-6">
                Tell us about your skills and experience.
              </p>
            </div>

            <div>
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={stepTwoData.bio}
                onChange={(e) => setStepTwoData({ ...stepTwoData, bio: e.target.value })}
                placeholder="Tell us about yourself, your background, and what you're looking for..."
                className="mt-2 min-h-[120px]"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {stepTwoData.bio.length}/500 characters
              </p>
              {validationErrors.bio && (
                <p className="text-sm text-destructive mt-1">{validationErrors.bio}</p>
              )}
            </div>

            <div>
              <Label htmlFor="experience_years">Years of Experience</Label>
              <Input
                id="experience_years"
                type="number"
                min="0"
                max="60"
                value={stepTwoData.experience_years}
                onChange={(e) => setStepTwoData({ ...stepTwoData, experience_years: parseInt(e.target.value) || 0 })}
                className="mt-2"
              />
              {validationErrors.experience_years && (
                <p className="text-sm text-destructive mt-1">{validationErrors.experience_years}</p>
              )}
            </div>

            <div>
              <Label htmlFor="skills">Skills *</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  id="skills"
                  value={stepTwoData.skillInput}
                  onChange={(e) => setStepTwoData({ ...stepTwoData, skillInput: e.target.value })}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addSkill();
                    }
                  }}
                  placeholder="Add a skill (e.g., Customer Service)"
                />
                <Button type="button" onClick={addSkill} variant="outline">
                  Add
                </Button>
              </div>
              {validationErrors.skills && (
                <p className="text-sm text-destructive mt-1">{validationErrors.skills}</p>
              )}
              
              {stepTwoData.skills.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {stepTwoData.skills.map((skill, index) => (
                    <div
                      key={index}
                      className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      {skill}
                      <button
                        type="button"
                        onClick={() => removeSkill(index)}
                        className="hover:text-destructive"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(1)}
                className="flex-1"
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button onClick={handleStepTwo} className="flex-1">
                Continue
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Social Links */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-4">Social Links</h3>
              <p className="text-muted-foreground mb-6">
                Add your professional profiles (optional).
              </p>
            </div>

            <div>
              <Label htmlFor="linkedin_url">LinkedIn Profile</Label>
              <Input
                id="linkedin_url"
                value={stepThreeData.linkedin_url}
                onChange={(e) => setStepThreeData({ ...stepThreeData, linkedin_url: e.target.value })}
                placeholder="https://linkedin.com/in/yourprofile"
                className="mt-2"
              />
              {validationErrors.linkedin_url && (
                <p className="text-sm text-destructive mt-1">{validationErrors.linkedin_url}</p>
              )}
            </div>

            <div>
              <Label htmlFor="github_url">GitHub Profile</Label>
              <Input
                id="github_url"
                value={stepThreeData.github_url}
                onChange={(e) => setStepThreeData({ ...stepThreeData, github_url: e.target.value })}
                placeholder="https://github.com/yourusername"
                className="mt-2"
              />
              {validationErrors.github_url && (
                <p className="text-sm text-destructive mt-1">{validationErrors.github_url}</p>
              )}
            </div>

            <div>
              <Label htmlFor="website_url">Personal Website</Label>
              <Input
                id="website_url"
                value={stepThreeData.website_url}
                onChange={(e) => setStepThreeData({ ...stepThreeData, website_url: e.target.value })}
                placeholder="https://yourwebsite.com"
                className="mt-2"
              />
              {validationErrors.website_url && (
                <p className="text-sm text-destructive mt-1">{validationErrors.website_url}</p>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(2)}
                className="flex-1"
                disabled={isLoading}
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button
                onClick={handleFinalSubmit}
                className="flex-1"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Complete Profile
                    <Check className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
